import { dottedClinicalCode, normaliseClinicalCode } from "@smartmed/codes";

import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { DuplicateCode, UploadCode } from "./types";

export const formatCode = (code: string) => dottedClinicalCode(normaliseClinicalCode(code));

export const toSentenceCase = (text: string) => {
  // Special case: preserve "NEC" in uppercase
  if (text === "NEC") return text;

  // If text is all uppercase, convert to sentence case
  if (text === text.toUpperCase()) {
    return text.toLowerCase().replace(/(^\w|\.\s+\w)/g, (letter) => letter.toUpperCase());
  }

  // Otherwise return the original text
  return text;
};

export const checkForDuplicates = async (codes: UploadCode[]): Promise<DuplicateCode[]> => {
  console.log(`=== Starting Duplicate Check ===`);
  console.log(`Input codes:`, codes.length);

  if (!codes.length) {
    console.error("No codes provided for duplicate check");
    return [];
  }

  const duplicates: DuplicateCode[] = [];
  const uniqueCodes = new Map<string, UploadCode>();

  // Check for duplicates within batch by code only (type is handled by separate tables)
  codes.forEach((code) => {
    // Format the code before checking for duplicates
    const formattedCode = formatCode(code.code);

    if (uniqueCodes.has(formattedCode.toUpperCase())) {
      console.log(`Found in-batch duplicate: ${formattedCode}`);
      duplicates.push({
        code: formattedCode,
        type: code.type,
        existingDescription: uniqueCodes.get(formattedCode.toUpperCase())!.description,
        newDescription: code.description,
      });
    }
    uniqueCodes.set(formattedCode.toUpperCase(), {
      ...code,
      code: formattedCode, // Store the formatted code
    });
  });

  try {
    // Check against database in batches
    console.log("Checking database for duplicates...");
    const batchSize = 100;

    for (let i = 0; i < codes.length; i += batchSize) {
      const batch = codes.slice(i, i + batchSize);

      // Split batch by type
      const icd10Codes = batch.filter((code) => code.type === "ICD-10");
      const opcs4Codes = batch.filter((code) => code.type === "OPCS-4");

      // Check ICD-10 codes
      if (icd10Codes.length > 0) {
        const formattedCodes = icd10Codes.map((code) => formatCode(code.code));
        const { data: existingICD10Codes, error: icd10Error } = await supabase
          .from("icd10_codes")
          .select("code, description")
          .in("code", formattedCodes);

        if (icd10Error) {
          console.error("ICD-10 database check error:", icd10Error);
          throw icd10Error;
        }

        if (existingICD10Codes) {
          existingICD10Codes.forEach((existingCode) => {
            const uploadCode = icd10Codes.find((code) => formatCode(code.code) === existingCode.code);

            if (uploadCode && existingCode.description !== uploadCode.description) {
              console.log(`Found ICD-10 DB duplicate with different description: ${uploadCode.code}`);
              duplicates.push({
                code: formatCode(uploadCode.code),
                type: "ICD-10",
                existingDescription: existingCode.description,
                newDescription: uploadCode.description,
              });
            }
          });
        }
      }

      // Check OPCS-4 codes
      if (opcs4Codes.length > 0) {
        const formattedCodes = opcs4Codes.map((code) => formatCode(code.code));
        const { data: existingOPCS4Codes, error: opcs4Error } = await supabase
          .from("opcs4_codes")
          .select("code, description")
          .in("code", formattedCodes);

        if (opcs4Error) {
          console.error("OPCS-4 database check error:", opcs4Error);
          throw opcs4Error;
        }

        if (existingOPCS4Codes) {
          existingOPCS4Codes.forEach((existingCode) => {
            const uploadCode = opcs4Codes.find((code) => formatCode(code.code) === existingCode.code);

            if (uploadCode && existingCode.description !== uploadCode.description) {
              console.log(`Found OPCS-4 DB duplicate with different description: ${uploadCode.code}`);
              duplicates.push({
                code: uploadCode.code,
                type: "OPCS-4",
                existingDescription: existingCode.description,
                newDescription: uploadCode.description,
              });
            }
          });
        }
      }
    }
  } catch (error) {
    console.error("Error during duplicate check:", error);
    throw new Error("Failed to check for duplicates in database");
  }

  console.log(`=== Duplicate Check Complete ===`);
  console.log(`Found ${duplicates.length} duplicates`);
  return duplicates;
};

export const processCodeUpload = async (
  codes: UploadCode[],
  duplicates: DuplicateCode[],
  descriptionChoices?: Record<string, "existing" | "new">,
  opcs4Version: number = 11,
) => {
  console.log(`=== Starting Code Upload Process ===`);
  console.log(`Total codes:`, codes.length);
  console.log(`Duplicates:`, duplicates.length);
  console.log(`Description choices:`, descriptionChoices);

  if (!codes.length) {
    throw new Error("No codes provided for upload");
  }

  try {
    // Create upload record
    console.log("Creating upload record...");
    const { data: uploadRecord, error: uploadRecordError } = await supabase
      .from("clinical_code_uploads")
      .insert({
        filename: "spreadsheet-upload",
        total_codes: codes.length,
        duplicates_found: duplicates.length,
        duplicates_skipped: duplicates.filter((d) => descriptionChoices?.[d.code] === "existing").length,
      })
      .select()
      .single();

    if (uploadRecordError) {
      console.error("Upload record creation error:", uploadRecordError);
      throw uploadRecordError;
    }

    // Filter codes to process, handling duplicates according to user choices
    const codesToProcess = codes.filter((code) => {
      // Format the code for consistent comparison
      const formattedCode = formatCode(code.code);

      const isDuplicate = duplicates.some(
        (d) => d.code.toUpperCase() === formattedCode.toUpperCase() && d.type === code.type,
      );

      // Skip if it's a duplicate and user chose to keep existing
      const shouldSkip = isDuplicate && descriptionChoices?.[formattedCode] === "existing";
      if (shouldSkip) {
        console.log(`Skipping duplicate: ${formattedCode}`);
      }
      return !shouldSkip;
    });

    console.log(`Processing ${codesToProcess.length} codes after filtering`);

    // Split codes by type
    const icd10Codes = codesToProcess.filter((code) => code.type === "ICD-10");
    const opcs4Codes = codesToProcess.filter((code) => code.type === "OPCS-4");

    // Process in smaller batches to avoid timeouts
    const batchSize = 50;
    let processedCount = 0;

    // Process ICD-10 codes
    for (let i = 0; i < icd10Codes.length; i += batchSize) {
      const batch = icd10Codes.slice(i, i + batchSize);
      console.log(`Processing ICD-10 batch ${i / batchSize + 1}, size: ${batch.length}`);

      const upsertData = batch.map((code) => ({
        code: formatCode(code.code),
        description: toSentenceCase(code.description),
      }));

      console.log("Upserting ICD-10 batch...");
      const { error } = await supabase.from("icd10_codes").upsert(upsertData, {
        onConflict: "code",
        ignoreDuplicates: false,
      });

      if (error) {
        console.error(`ICD-10 batch processing error:`, error);
        throw error;
      }

      processedCount += batch.length;
    }

    // Process OPCS-4 codes
    for (let i = 0; i < opcs4Codes.length; i += batchSize) {
      const batch = opcs4Codes.slice(i, i + batchSize);
      console.log(`Processing OPCS-4 batch ${i / batchSize + 1}, size: ${batch.length}`);

      const upsertData = batch.map((code) => ({
        code: formatCode(code.code),
        description: toSentenceCase(code.description),
        version: opcs4Version,
      }));

      console.log("Upserting OPCS-4 batch...");
      const { error } = await supabase.from("opcs4_codes").upsert(upsertData, {
        onConflict: "code",
        ignoreDuplicates: false,
      });

      if (error) {
        console.error(`OPCS-4 batch processing error:`, error);
        throw error;
      }

      processedCount += batch.length;

      // Show progress for large uploads
      if (processedCount % 500 === 0) {
        console.log(`Progress: ${processedCount}/${codesToProcess.length}`);
        toast(`Processed ${processedCount} of ${codesToProcess.length} codes...`);
      }
    }

    // Update final status
    console.log("Updating upload record status...");
    await supabase
      .from("clinical_code_uploads")
      .update({
        status: "completed",
        duplicates_updated: processedCount,
      })
      .eq("id", uploadRecord.id);

    console.log(`=== Upload Process Complete ===`);
    console.log(`Total processed: ${processedCount}`);
    return processedCount;
  } catch (error) {
    console.error("=== Upload Process Failed ===");
    console.error(error);
    throw error;
  }
};
